import { useState } from "react";
import { Link, useParams } from "react-router-dom";

import { useSeo } from "../../lib/useSeo";
import { DOC_TOPICS, DOCS_META, findTopic, type DocTopic } from "./data/docs";

/**
 * NestPlot public documentation — /docs and /docs/:slug.
 *
 * Content lives in ./data/docs.ts; this renders it with a light
 * markdown-ish pass (### heading, "- " bullet, "> " callout, **bold**,
 * `code`). Rendered inside MarketingShell via the route Outlet, so this
 * returns page content only (the shell adds the nav + footer).
 */

// **bold** and `code` in one pass.
function renderInline(text: string, keyBase: string) {
  const parts = String(text).split(/(\*\*.+?\*\*|`[^`]+`)/g);
  return parts.map((p, i) => {
    if (p.startsWith("**") && p.endsWith("**")) {
      return (
        <strong key={`${keyBase}-${i}`} className="text-ink font-bold">
          {p.slice(2, -2)}
        </strong>
      );
    }
    if (p.startsWith("`") && p.endsWith("`") && p.length > 2) {
      return (
        <code
          key={`${keyBase}-${i}`}
          className="font-mono text-[0.88em] bg-surface border border-line rounded px-[5px] py-[1px] text-ink"
        >
          {p.slice(1, -1)}
        </code>
      );
    }
    return <span key={`${keyBase}-${i}`}>{p}</span>;
  });
}

function Body({ lines }: { lines: string[] }) {
  const out: React.ReactNode[] = [];
  let list: string[] | null = null;
  const flush = () => {
    if (list) {
      const items = list;
      out.push(
        <ul key={`ul-${out.length}`} className="mb-4 pl-6 flex flex-col gap-[7px] list-disc">
          {items.map((li, i) => (
            <li key={i} className="text-[14.5px] text-sub leading-[1.6]">
              {renderInline(li, `li-${out.length}-${i}`)}
            </li>
          ))}
        </ul>,
      );
      list = null;
    }
  };
  lines.forEach((line, idx) => {
    if (line.startsWith("- ")) {
      list = list || [];
      list.push(line.slice(2));
      return;
    }
    flush();
    if (line.startsWith("### ")) {
      out.push(
        <h3 key={idx} className="text-[17px] font-extrabold text-ink mt-[26px] mb-2 tracking-[-0.01em]">
          {line.slice(4)}
        </h3>,
      );
    } else if (line.startsWith("> ")) {
      out.push(
        <div
          key={idx}
          className="bg-paper border border-accent/40 rounded-lg px-[14px] py-[10px] mb-4 text-[13.5px] text-ink leading-[1.55]"
        >
          {renderInline(line.slice(2), `co-${idx}`)}
        </div>,
      );
    } else {
      out.push(
        <p key={idx} className="text-[14.5px] text-sub leading-[1.65] mb-4">
          {renderInline(line, `p-${idx}`)}
        </p>,
      );
    }
  });
  flush();
  return <>{out}</>;
}

function TopicCard({ t }: { t: DocTopic }) {
  return (
    <Link to={`/docs/${t.slug}`} className="no-underline">
      <div className="bg-paper border border-line rounded-xl p-5 h-full flex flex-col gap-1.5 hover:border-accent transition-colors">
        <div className="text-2xl">{t.icon}</div>
        <div className="text-[16px] font-extrabold text-ink tracking-[-0.01em]">{t.title}</div>
        <div className="text-[13px] text-sub leading-[1.5]">{t.summary}</div>
      </div>
    </Link>
  );
}

function Sidebar({ current }: { current?: string }) {
  return (
    <aside className="hidden lg:block sticky top-[90px] self-start">
      <Link
        to="/docs"
        className="font-mono text-[12px] font-bold text-sub no-underline tracking-[0.08em] uppercase"
      >
        ← All docs
      </Link>
      <nav className="flex flex-col gap-0.5 mt-3">
        {DOC_TOPICS.map((t) => (
          <Link
            key={t.slug}
            to={`/docs/${t.slug}`}
            className={
              "text-[13px] no-underline px-[10px] py-[6px] rounded-md " +
              (t.slug === current
                ? "text-accent font-bold bg-paper"
                : "text-sub font-medium hover:bg-paper")
            }
          >
            {t.title}
          </Link>
        ))}
      </nav>
    </aside>
  );
}

export function DocsPage() {
  const { slug } = useParams();
  const topic = slug ? findTopic(slug) : undefined;
  const [q, setQ] = useState("");

  useSeo(
    topic
      ? {
          title: `${topic.title} — NestPlot docs`,
          description: topic.summary,
          path: `/docs/${topic.slug}`,
        }
      : { title: DOCS_META.title, description: DOCS_META.description, path: "/docs" },
  );

  // Unknown slug → friendly 404.
  if (slug && !topic) {
    return (
      <div className="max-w-[720px] mx-auto px-6 py-16">
        <h1 className="text-2xl font-extrabold text-ink">That page doesn't exist</h1>
        <Link to="/docs" className="text-accent text-sm">
          ← Back to the docs
        </Link>
      </div>
    );
  }

  // Topic view.
  if (topic) {
    const idx = DOC_TOPICS.findIndex((t) => t.slug === topic.slug);
    const prev = DOC_TOPICS[idx - 1];
    const next = DOC_TOPICS[idx + 1];
    return (
      <div className="mx-auto max-w-[1060px] px-6 pt-9 pb-20 grid lg:grid-cols-[230px_1fr] gap-10">
        <Sidebar current={topic.slug} />
        <article className="min-w-0">
          <div className="font-mono text-[11px] text-sub tracking-[0.1em] uppercase mb-3">
            <Link to="/docs" className="hover:text-ink no-underline text-sub">
              Docs
            </Link>{" "}
            / {topic.title}
          </div>
          <h1 className="text-[30px] font-extrabold text-ink tracking-[-0.02em] mb-2">
            <span className="mr-2">{topic.icon}</span>
            {topic.title}
          </h1>
          <p className="text-[15px] text-sub leading-[1.6] mb-7">{topic.summary}</p>
          <Body lines={topic.body} />
          <div className="flex justify-between gap-4 mt-12 pt-6 border-t border-line">
            {prev ? (
              <Link to={`/docs/${prev.slug}`} className="text-accent text-sm no-underline">
                ← {prev.title}
              </Link>
            ) : (
              <span />
            )}
            {next ? (
              <Link to={`/docs/${next.slug}`} className="text-accent text-sm no-underline text-right">
                {next.title} →
              </Link>
            ) : (
              <span />
            )}
          </div>
        </article>
      </div>
    );
  }

  // Index view.
  const ql = q.trim().toLowerCase();
  const filtered = ql
    ? DOC_TOPICS.filter((t) => {
        const hay = `${t.title} ${t.summary} ${t.body.join(" ")}`.toLowerCase();
        return ql.split(/\s+/).every((term) => hay.includes(term));
      })
    : DOC_TOPICS;

  return (
    <div className="mx-auto max-w-[1060px] px-6 pt-12 pb-20">
      <h1 className="text-[34px] font-extrabold text-ink tracking-[-0.02em]">Documentation</h1>
      <p className="text-[16px] text-sub leading-[1.6] mt-3 max-w-[640px]">{DOCS_META.description}</p>
      <input
        type="search"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search the docs…"
        className="mt-6 w-full max-w-[420px] rounded-lg border border-line bg-paper px-4 py-2.5 text-[14px] text-ink placeholder:text-sub outline-none focus:border-accent"
      />
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-8">
        {filtered.map((t) => (
          <TopicCard key={t.slug} t={t} />
        ))}
      </div>
      {filtered.length === 0 && (
        <p className="text-sub text-sm mt-8">No topics match “{q}”. Try fewer words.</p>
      )}
      <div className="mt-14 bg-paper border border-line rounded-xl p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="text-ink font-extrabold text-[17px]">Can't find it?</div>
          <div className="text-sub text-[14px] mt-1">
            Email info@nestplot.com or book a 20-minute walkthrough — we respond within the hour.
          </div>
        </div>
        <Link
          to="/book-demo"
          className="shrink-0 inline-flex items-center justify-center rounded-md bg-accent text-on-accent font-semibold text-sm px-4 py-2.5 no-underline"
        >
          Book a demo
        </Link>
      </div>
    </div>
  );
}
